import React, { useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';
import { bindActionCreators } from 'redux';
import Bullets from 'slickpay/common/Bullets.component';

import ICO_VOUCHERS from 'assets/images/online-booking/e-gift-vouchers-purple.svg';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import Toggler from 'components/form/Toggler.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import CurrencySign from 'components/price/CurrencySign.component';
import DELAY from 'constants/delay.const';
import { GIFT_VOUCHER_IMAGES } from 'constants/images/onlineBooking.const';
import LINK from 'constants/link.const';
import fromDtoToNcobEgiftSettings from 'dto/settings/fromDtoToNcobEgiftSettings.dto';
import useAfn from 'hooks/useAfn.hook';
import { useCountryImage } from 'hooks/useCountryImage.hook';
import { useHelpLinks } from 'hooks/useHelpLinks.hook';
import { useNcobRedirectUrl } from 'hooks/useNcobRedirectUrl.hook';
import useTip from 'hooks/useTip.hook';
import { useDispatcher } from 'providers/Dispatcher.provider';
import { useTranslation } from 'providers/GILT.provider';
import { useNcobSettings } from 'providers/NcobSettings.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import getString from 'utils/accessors/getString.util';
import cpSet from 'utils/dom/clipboard/cpSet.util';
import delayed from 'utils/functions/delayed.util';
import linkTo from 'utils/links/linkTo.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import C from 'utils/values/constant.util';

import beDisableCardCapability from 'settings/actions/beDisableCardCapability.action';
import { getSalonOnStripeDashboard } from 'settings/online-booking/actions';
import AfterEnableVoucherModal from 'settings/online-booking/modals/AfterEnableVoucherModal';
import BeforeSetupStripeModal from 'settings/online-booking/modals/BeforeSetupStripeModal';
import StripeModal from 'settings/page-elements/setup-nsp-deposit/modals/StripeModal';

import CN from './EVouchersPage.module.scss';

const L = logger('EVouchersPage');

const STRIPE_ACCOUNT_STATUS = Object.freeze({
  active: 'active',
  restricted: 'restricted',
});

const LINK_TERMS = 'https://www.getslick.com/legal/terms';
const USE_SETUP_STRIPE = false;

const getStripeNotificationSteps = (t) =>
  C([t('onlineBooking.eVauchers.bullets.login'), t('onlineBooking.eVauchers.bullets.settings'), t('onlineBooking.eVauchers.bullets.preferences'), t('onlineBooking.eVauchers.bullets.notifications')]);

const EVouchersPage = ({ getSalonOnStripeDashboard }) => {
  const salonId = useCurrentSalonId();
  const { isDarkMode } = useTheme();
  const dispatchBeDisableCardCapability = useDispatcher(beDisableCardCapability);
  const { t } = useTranslation();
  const { eVouchersHelpLink } = useHelpLinks();
  const eGiftVoucherImage = useCountryImage(GIFT_VOUCHER_IMAGES);

  const { value: valueSettings, isLoading: isLoadingSettings, error: errorSettings, read, updateEgift } = useNcobSettings();

  const { hasNcobStripeAccount, ncobTinyUrl, canTakeVoucherPayments } = asObject(valueSettings);
  const effectiveNcobUrl = useNcobRedirectUrl({ ncobTinyUrl, salonId });
  const isCopyUrlDisabled = !canTakeVoucherPayments;

  const { val: isBeforeSetupRendered, yay: showBeforeSetup, nay: hideBeforeSetup } = useTip(hasNcobStripeAccount);
  const { val: isBeingCopied, yay: startCopying, nay: stopCopying } = useTip(false);
  const { val: isStripeModalRendered, yay: showStripe } = useTip(false);
  const { val: isIncompleteInfoOpen, yay: showIncompleteInfo, nay: hideIncompleteInfo } = useTip(false);

  const [errorMessage, setErrorMessage] = useState('');
  const { val: renderErrorModal, yay: showModal, nay: hideModal } = useTip(false);

  const linkVouchers = linkTo({ pattern: LINK.vouchersTracking, params: { salonId }, fault: '#' });
  const linkCashup = linkTo({ pattern: LINK.cashup, params: { salonId }, fault: '#' });
  const stripeNotificationSteps = useMemo(() => getStripeNotificationSteps(t), [t]);

  const [{ value: valueEgift, isLoading: isLoadingEgift, error: errorEgift }, changeEgift] = useAfn(async () => {
    const newCanTake = !canTakeVoucherPayments;
    const r = await updateEgift({ data: { canTakeVoucherPayments: newCanTake } });
    L.debug('changeEgift()', r);

    const value = fromDtoToNcobEgiftSettings(r.json);
    if (!r.ok) {
      if (r?.result?.json._code === 'SETTINGS_165') {
        setErrorMessage(r.result.json._message);
        showModal();
        return r.result.json._message;
      }
    }
    const { stripeAccountStatus } = asObject(value);
    const isRestricted = stripeAccountStatus === STRIPE_ACCOUNT_STATUS.restricted;
    if (isRestricted) showIncompleteInfo();

    return value;
  }, [canTakeVoucherPayments, showIncompleteInfo, showModal, updateEgift]);

  const [{ isLoading: isLoadingLink, error: errorLink, value: link }, fetchLink] = useAfn(async () => {
    const result = await getSalonOnStripeDashboard(salonId);

    return result.is.ok ? getString('json.url', result) : '';
  }, [salonId, getSalonOnStripeDashboard]);

  const handleConfirmVoucher = useCallback(() => {
    fetchLink();
    changeEgift();
  }, [changeEgift, fetchLink]);

  const [{ error: errorCopy }, handleCopy] = useAfn(async () => {
    await cpSet(effectiveNcobUrl);
    startCopying();
    delayed(DELAY.attention, stopCopying);
  }, [startCopying, effectiveNcobUrl, stopCopying]);

  const [{ isLoading: isLoadingReject, error: errorReject }, handleReject] = useAfn(async () => {
    hideIncompleteInfo();
    const resultDisable = await dispatchBeDisableCardCapability();
    L.debug('handleReject()', 'resultDisable:', resultDisable);

    const resultRead = await read({ force: true }); // NOTE: force means to not use cached value, but to fetch from BE
    L.debug('handleReject()', 'resultRead:', resultRead);
  }, [dispatchBeDisableCardCapability, hideIncompleteInfo, read]);

  const isLoading = isLoadingSettings || isLoadingEgift || isLoadingLink || isLoadingReject;
  const error = errorSettings || errorEgift || errorLink || errorCopy || errorReject;
  if (error) L.warn('()', { isLoading, error, valueSettings, valueEgift, link });

  L.debug('()', isLoading, error, { canTakeVoucherPayments, valueSettings, valueEgift, link });

  return (
    <div
      data-bem="EVouchersPage"
      className={cn({
        [CN.component]: true,
        [CN.darkMode]: isDarkMode,
      })}
    >
      <div data-bem="EVouchersPage__title" className={CN.pageTitle}>
        <img src={ICO_VOUCHERS} alt={t('onlineBooking.eVauchers.giftVoucherIcon')} className={CN.menuIcon} />
        {t('onlineBooking.eVauchers.pageTitle')}
      </div>
      <div className={CN.container}>
        <div className={CN.content}>
          <div className={CN.title}>
            <div>{t('onlineBooking.eVauchers.sellVouchers')}</div>
            {hasNcobStripeAccount && (
              <div className={CN.togglerContainer}>
                <Toggler onClick={handleConfirmVoucher} selected={canTakeVoucherPayments} />
              </div>
            )}
          </div>
          {hasNcobStripeAccount ? (
            <>
              <div className={CN.text}>
                {t('onlineBooking.eVauchers.stripeDescription1Part1')} <CurrencySign />
                {t('onlineBooking.eVauchers.stripeDescription1Part2')} <CurrencySign />
                {t('onlineBooking.eVauchers.stripeDescription1Part3')}
              </div>
              <div className={CN.text}>
                {t('onlineBooking.eVauchers.stripeDescription2Part1')}
                <NavLink to={linkCashup}>
                  <span className={CN.urlText}>{t('onlineBooking.eVauchers.stripeDescription2Part2')}</span>
                </NavLink>
                {t('onlineBooking.eVauchers.stripeDescription2Part3')}
                <NavLink to={linkVouchers}>
                  <span className={CN.urlText}>{t('onlineBooking.eVauchers.stripeDescription2Part4')}</span>
                </NavLink>
              </div>
              <div className={CN.urlTextContainer}>
                {t('onlineBooking.eVauchers.stripeDescription3')}
                <span className={canTakeVoucherPayments ? CN.urlText : CN.disabledUrlText}>{effectiveNcobUrl}</span>
              </div>

              <div data-bem="EVouchersPage__instructions-with-stripe-account">
                <div className={CN.text}>{t('onlineBooking.eVauchers.stripeInfo.payoutInfo')}</div>
                <div className={CN.text}>
                  {t('onlineBooking.eVauchers.stripeInfo.costIntro')} 0.004%! {t('onlineBooking.eVauchers.stripeInfo.costExamplePrefix')} <CurrencySign />
                  930 {t('onlineBooking.eVauchers.stripeInfo.costExampleMiddle')} <CurrencySign />
                  0.37 {t('onlineBooking.eVauchers.stripeInfo.costExampleSuffix')}
                </div>
                <div className={CN.text}>{t('onlineBooking.eVauchers.stripeInfo.notificationSteps')}</div>
                <Bullets tag="EVouchersPage" data={stripeNotificationSteps} empty compact />
              </div>
            </>
          ) : (
            <div className={CN.text}>
              {t('onlineBooking.eVauchers.noStripeDescription1Part1')}
              <ExternalLink variant="inline" link={LINK_TERMS}>
                <div className={CN.linkText}>{t('onlineBooking.eVauchers.noStripeDescription1Part2')}</div>
              </ExternalLink>
            </div>
          )}

          {!hasNcobStripeAccount && <div className={CN.info}>{t('onlineBooking.eVauchers.noStripeDescription2')}</div>}

          <div className={CN.buttonsContainer}>
            {hasNcobStripeAccount ? (
              <Button variant="primary" onClick={handleCopy} disabled={isCopyUrlDisabled}>
                {isBeingCopied ? t('onlineBooking.eVauchers.buttons.copied') : t('onlineBooking.eVauchers.buttons.copyUrl')}
              </Button>
            ) : (
              <>
                {USE_SETUP_STRIPE ? (
                  <Button variant="primary" onClick={isBeforeSetupRendered ? showStripe : showBeforeSetup}>
                    <div className={CN.flex}>
                      <Icon variant="stripeWhite" />
                      <div className={CN.spaceLeft}>{t('onlineBooking.eVauchers.buttons.setup')}</div>
                    </div>
                  </Button>
                ) : (
                  <div />
                )}
              </>
            )}

            <ExternalLink variant="inline" link={eVouchersHelpLink}>
              <div
                className={cn({
                  [CN.link]: true,
                  [CN.linkSpace]: hasNcobStripeAccount,
                })}
              >
                {t('onlineBooking.eVauchers.buttons.needHelp')}
              </div>
            </ExternalLink>
          </div>
        </div>
        <div className={CN.imageContainer}>
          <img src={eGiftVoucherImage} alt={t('onlineBooking.eVauchers.voucherImage')} className={CN.image} />
        </div>
      </div>

      <AfterEnableVoucherModal isOpen={isIncompleteInfoOpen} dashboardLink={link} onReject={handleReject} onDashboardLink={hideIncompleteInfo} />

      {isStripeModalRendered && (
        <StripeModal>
          <div className={CN.textCenter}>{t('onlineBooking.eVauchers.stripeModalText')}</div>
        </StripeModal>
      )}

      <BeforeSetupStripeModal isOpen={isBeforeSetupRendered && !hasNcobStripeAccount} onClose={hideBeforeSetup} />

      {isLoading && <OverlaySpinner />}

      <SalonModal tag="EVouchersPage__modal--error" isOpen={renderErrorModal} variant="noFooter">
        <SalonModal.Header>
          <div className={CN.closeButtonContainer}>
            <Button variant="empty" onClick={hideModal}>
              <Icon variant="close" variantInvert />
            </Button>
          </div>
        </SalonModal.Header>
        <SalonModal.Body>
          <div className={CN.modalContent}>
            <IconStatus variant="alert">
              <Icon variant="alert" />
            </IconStatus>
            <div className={CN.modalTitle}>{t('onlineBooking.eVauchers.errorModalText')}</div>
            <span className={CN.modalText}>{errorMessage}</span>
          </div>
        </SalonModal.Body>
      </SalonModal>
    </div>
  );
};

const mapStateToProps = (state) => ({
  dashboardUrl: state.settings.onlineBooking.dashboardUrl,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getSalonOnStripeDashboard,
    },
    dispatch,
  );

const EVouchersPageHoc = connect(mapStateToProps, mapDispatchToProps)(EVouchersPage);

export default EVouchersPageHoc;
